// Tab "Lần đến": lịch sử khách đến cơ sở – dịch vụ đã làm, KTV, chữ ký xác nhận, đánh giá, ảnh của từng lần
import { useState } from 'react';
import { Modal, Rate, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import { Camera, FileCheck2, FileImage, PenLine } from 'lucide-react';
import { branchName } from '../POS/data';
import { useTreatmentAuth } from '../Treatment/actions';
import { Kpi, Pill, fmtDateTime } from '../Treatment/ui';
import { VISIT_STATUS, type Visit } from './logic';
import { useMedia } from './mediaDb';
import { Btn, LinkBar, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

const WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
type Filter = 'all' | 'pending' | 'signed' | 'paper';

export function VisitStatusPill({ v }: { v: Visit }) {
  if (v.overdue) return <Pill label={`Quá hạn ký (${v.age} ngày)`} cls="bg-red-600 text-white border-red-600" />;
  return <Pill {...VISIT_STATUS[v.status]} />;
}

export default function VisitsPanel({ c, ops, onViewDoc }: { c: C360; ops: C360Ops; onViewDoc: (id: string) => void }) {
  const { can } = useTreatmentAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const [paper, setPaper] = useState<string | null>(null);
  const need = c.visits.filter(v => v.status !== 'none');
  const ok = need.filter(v => v.status === 'signed' || v.status === 'paper').length;
  const waiting = need.filter(v => v.status === 'pending' || v.status === 'partial');
  const rated = c.visits.filter(v => v.rating);
  const avg = rated.length ? rated.reduce((s, v) => s + (v.rating || 0), 0) / rated.length : 0;
  const list = c.visits.filter(v => filter === 'all'
    || (filter === 'pending' ? v.status === 'pending' || v.status === 'partial' : v.status === filter));
  const today = c.visits.find(v => v.date === dayjs().format('YYYY-MM-DD'));

  return (
    <div className="space-y-4">
      <LinkBar c={c} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Số lần đến" value={c.visits.length} sub={c.visits[0] ? `Gần nhất ${dayjs(c.visits[0].date).format('DD/MM/YYYY')}` : 'Chưa đến lần nào'} />
        <Kpi label="Đã ký xác nhận" value={`${ok}/${need.length}`} tone={need.length && ok === need.length ? 'ok' : 'default'} sub={need.length ? `${Math.round((ok / need.length) * 100)}% lần đến có làm dịch vụ` : ' '} />
        <Kpi label="Chờ ký" value={waiting.length} tone={waiting.some(v => v.overdue) ? 'danger' : waiting.length ? 'warn' : 'ok'} sub={waiting.some(v => v.overdue) ? `${waiting.filter(v => v.overdue).length} lần quá hạn ký` : 'Không quá hạn'} onClick={() => setFilter('pending')} />
        <Kpi label="Khách đánh giá" value={avg ? `${avg.toFixed(1)} ★` : '—'} sub={`${rated.length} lượt đánh giá`} />
      </div>

      {today && (today.status === 'pending' || today.status === 'partial') && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <PenLine className="h-5 w-5 text-amber-700" />
          <div className="min-w-0 flex-1 text-sm text-amber-900">
            <b>Lần đến hôm nay chưa ký xác nhận</b> – {today.unsigned.length} dịch vụ: {today.unsigned.map(i => i.title).join(', ')}
          </div>
          {can('create') && <Btn primary onClick={() => ops.signVisit(today.date)}>Hoàn tất lần đến</Btn>}
        </div>
      )}

      <Segmented value={filter} onChange={x => setFilter(x as Filter)} options={[
        { value: 'all', label: `Tất cả (${c.visits.length})` }, { value: 'pending', label: `Chờ ký (${waiting.length})` },
        { value: 'signed', label: 'Đã ký' }, { value: 'paper', label: 'Ký giấy' },
      ]} />

      {!list.length && <div className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400">Không có lần đến</div>}

      <div className="space-y-3">
        {list.map(v => {
          const d = dayjs(v.date);
          const firstUse = v.items.find(i => i.kind === 'use');
          return (
            <div key={v.date} className={`flex gap-3 rounded-xl border bg-white p-3 ${v.overdue ? 'border-red-200' : 'border-slate-200'}`}>
              <div className="w-14 shrink-0 rounded-lg bg-[#ecf6f9] py-1.5 text-center">
                <div className="text-[11px] font-semibold text-[#2c6b7f]">{WEEKDAY[d.day()]}</div>
                <div className="text-lg font-extrabold leading-tight text-slate-800">{d.format('DD')}</div>
                <div className="text-[11px] text-slate-500">{d.format('MM/YYYY')}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <VisitStatusPill v={v} />
                  {v.rating ? <Rate disabled value={v.rating} className="!text-sm" /> : null}
                  {v.deferred && v.status !== 'signed' && <span className="text-xs text-amber-700">Hẹn ký sau: {v.deferred.reason}</span>}
                  <span className="ml-auto text-xs text-slate-500">{dayjs(v.firstAt).format('HH:mm')}{v.lastAt !== v.firstAt ? `–${dayjs(v.lastAt).format('HH:mm')}` : ''} · {v.branchIds.map(branchName).join(', ')}</span>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {v.items.map(i => {
                    const dot = v.signedKeys.includes(i.key) ? 'bg-green-500' : v.paperKeys.includes(i.key) ? 'bg-slate-500' : i.needSign ? 'bg-amber-500' : 'bg-slate-300';
                    return (
                      <div key={i.key} className="flex gap-2 text-sm">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} title={v.signedKeys.includes(i.key) ? 'Đã ký' : v.paperKeys.includes(i.key) ? 'Ký giấy' : i.needSign ? 'Chưa ký' : 'Không cần ký'} />
                        <span className="min-w-0"><b className="font-medium text-slate-800">{i.title}</b> <span className="text-slate-500">· {i.detail}</span></span>
                      </div>
                    );
                  })}
                </div>
                {v.ratingNote && <div className="mt-1 text-xs italic text-slate-500">“{v.ratingNote}”</div>}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500">KTV {v.staff.join(', ') || '—'}</span>
                  {v.docs.map(doc => (
                    <button key={doc.id} onClick={() => onViewDoc(doc.id)} className="inline-flex items-center gap-1 font-semibold text-green-700 hover:underline">
                      <FileCheck2 className="h-3.5 w-3.5" /> {doc.id} · {fmtDateTime(doc.signedAt)}
                    </button>
                  ))}
                  {v.paperPhotoId && <button onClick={() => setPaper(v.paperPhotoId!)} className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:underline"><FileImage className="h-3.5 w-3.5" /> Ảnh phiếu giấy</button>}
                  <button onClick={() => ops.upload({ cardId: firstUse?.cardId, usageId: firstUse?.usageId })} className="inline-flex items-center gap-1 text-[#2c6b7f] hover:underline"><Camera className="h-3.5 w-3.5" /> Ảnh ({v.photos.length})</button>
                  {(v.status === 'pending' || v.status === 'partial') && can('create') && (
                    <span className="ml-auto"><Btn primary onClick={() => ops.signVisit(v.date)}>{v.status === 'partial' ? 'Ký bổ sung' : 'Ký xác nhận'}</Btn></span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <PaperViewer id={paper} onClose={() => setPaper(null)} />
    </div>
  );
}

function PaperViewer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const src = useMedia(id);
  return (
    <Modal open={!!id} onCancel={onClose} footer={null} centered width={720} title="Ảnh phiếu giấy khách đã ký">
      <div className="flex min-h-[300px] items-center justify-center rounded-lg bg-slate-900">{src ? <img src={src} alt="" className="max-h-[70vh] max-w-full object-contain" /> : <Spin />}</div>
    </Modal>
  );
}
